import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import API from '../api';
import { FEATURES } from '../config/features';
import {
  generateCacheKey,
  normalizeV2Response,
  calculateOptimalPageSize,
  createPaginationMeta,
  validatePaginationParams,
  mergePaginatedData,
  paginationCache
} from '../utils/pagination';

/**
 * Custom hook for fetching paginated products using V2 APIs
 * 
 * Features:
 * - Server-side pagination with nextToken support
 * - Intelligent caching with TTL
 * - Automatic fallback to V1 APIs
 * - Loading states and error handling
 * - Infinite scroll support
 * - Performance optimizations for mobile/slow connections
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.endpoint - V2 API endpoint to fetch from
 * @param {Object} options.params - Query parameters for the API request
 * @param {number} options.limit - Items per page (default: 100)
 * @param {boolean} options.useCache - Whether to use cache (default: true)
 * @param {boolean} options.autoLoad - Auto-load first page on mount (default: true)
 * @param {string} options.fallbackEndpoint - V1 endpoint for fallback
 * @param {boolean} options.enableInfiniteScroll - Enable infinite scroll behavior
 * @returns {Object} Hook state and methods
 */

/**
 * Check if an error is a request cancellation
 * @param {Error} error - The error to check
 * @returns {boolean} True if the error is a cancellation
 */
const isCancellationError = (error) => {
  return error.name === 'AbortError' || 
         error.name === 'CanceledError' || 
         error.code === 'ERR_CANCELED' ||
         (error.message && error.message.includes('canceled'));
};
export function usePaginatedProducts({
  endpoint = '/api/v2/products',
  params = {},
  limit = FEATURES.PAGINATION_SETTINGS.DEFAULT_LIMIT,
  useCache = true,
  autoLoad = true,
  fallbackEndpoint = null,
  enableInfiniteScroll = true
}) {
  // State management
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(autoLoad);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [nextToken, setNextToken] = useState(null);
  const [totalItems, setTotalItems] = useState(null);
  const [usingV1Fallback, setUsingV1Fallback] = useState(false);

  // Refs for request management
  const abortControllerRef = useRef(null);
  const requestIdRef = useRef(0);
  const retryCountRef = useRef(0);

  // Memoized values
  const stableParamsString = useMemo(() => {
    return Object.keys(params)
      .sort()
      .map(key => `${key}=${params[key]}`)
      .join('&');
  }, [params]);

  const isV2Enabled = useMemo(() => {
    if (!FEATURES.USE_V2_PAGINATION) return false;
    
    // Check specific endpoint flags
    if (endpoint.includes('/api/v2/products/search')) {
      return FEATURES.V2_ENDPOINTS.SEARCH;
    }
    if (endpoint.includes('/api/v2/products/by-category-and-countries')) {
      return FEATURES.V2_ENDPOINTS.CATEGORY_COUNTRIES;
    }
    if (endpoint.includes('/api/v2/products/by-category-subcategory-countries')) {
      return FEATURES.V2_ENDPOINTS.CATEGORY_SUBCATEGORY_COUNTRIES;
    }
    if (endpoint.includes('/api/v2/loved-products')) {
      return FEATURES.V2_ENDPOINTS.LOVED_PRODUCTS;
    }
    if (endpoint.includes('/api/v2/products')) {
      return FEATURES.V2_ENDPOINTS.PRODUCTS;
    }
    
    return false;
  }, [endpoint]);

  const optimizedLimit = useMemo(() => {
    // Detect mobile and connection type
    const isMobile = window.innerWidth <= 768;
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const connectionType = connection?.effectiveType || 'unknown';
    
    return calculateOptimalPageSize({
      defaultLimit: limit,
      isMobile,
      connectionType
    });
  }, [limit]);

  // Cache key generation
  const getCacheKey = useCallback((page = 1) => {
    return generateCacheKey(endpoint, params, page);
  }, [endpoint, params]);

  // Debug logging
  const debugLog = useCallback((message, data = null) => {
    // Only log critical errors and important pagination events in production
    if (FEATURES.DEBUG.LOG_PAGINATION && process.env.NODE_ENV === 'development') {
      // Don't spam console with cancellation messages unless explicitly debugging cancellations
      if (message.includes('canceled') && !process.env.REACT_APP_DEBUG_CANCELLATIONS) {
        return;
      }
      // Add timestamp to debug logs
      const timestamp = new Date().toISOString().substr(11, 12);
      // Limit data logging to prevent memory issues
      const logData = data && typeof data === 'object' ? 
        Object.keys(data).reduce((acc, key) => {
          if (key === 'response' || key === 'products') {
            acc[key] = `[${key.toUpperCase()}_OBJECT]`;
          } else {
            acc[key] = data[key];
          }
          return acc;
        }, {}) : data;
      console.log(`[${timestamp}] [usePaginatedProducts] ${message}`, logData);
    }
  }, []);

  // Abort current request
  const abortCurrentRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Fallback to V1 API
  const fallbackToV1 = useCallback(async (fallbackParams = {}) => {
    if (!fallbackEndpoint) {
      throw new Error('No fallback endpoint provided');
    }

    debugLog('Falling back to V1 API', { fallbackEndpoint, fallbackParams });
    setUsingV1Fallback(true);
    
    try {
      const response = await API.get(fallbackEndpoint, { 
        params: fallbackParams,
        signal: abortControllerRef.current?.signal
      });
      const data = Array.isArray(response.data) ? response.data : response.data.products || [];
      
      return {
        products: data,
        pagination: {
          hasMore: false,
          nextToken: null,
          total: data.length
        }
      };
    } catch (v1Error) {
      // Don't log fallback failures for canceled requests
      if (isCancellationError(v1Error)) {
        debugLog('V1 fallback request canceled');
        throw v1Error;
      }
      
      debugLog('V1 fallback also failed', v1Error);
      throw v1Error;
    }
  }, [fallbackEndpoint, debugLog]);

  // Core fetch function
  const fetchPage = useCallback(async (pageNumber = 1, token = null, shouldAppend = false) => {
    // Cancel any pending request
    abortCurrentRequest();
    
    // Create new abort controller
    abortControllerRef.current = new AbortController();
    const currentRequestId = ++requestIdRef.current;
    
    const isFirstPage = pageNumber === 1;
    const isLoadingMore = shouldAppend && !isFirstPage;
    
    if (isLoadingMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    
    setError(null);

    try {
      // Check cache first
      if (useCache && !token) {
        const cacheKey = getCacheKey(pageNumber);
        const cachedData = paginationCache.get(cacheKey);
        
        if (cachedData) {
          debugLog(`Cache hit for page ${pageNumber}`, { cacheKey });
          
          // Verify this is still the current request
          if (currentRequestId !== requestIdRef.current) return;
          
          const newProducts = shouldAppend 
            ? mergePaginatedData(cachedData.products, cachedData.products, 'replace')
            : cachedData.products;
          
          setProducts(prev => shouldAppend 
            ? mergePaginatedData(prev, cachedData.products, 'append')
            : cachedData.products
          );
          setHasMore(cachedData.pagination.hasMore);
          setNextToken(cachedData.pagination.nextToken);
          setCurrentPage(pageNumber);
          
          if (cachedData.pagination.total !== undefined) {
            setTotalItems(cachedData.pagination.total);
          }
          
          setLoading(false);
          setLoadingMore(false);
          return;
        }
      }

      // Prepare API parameters
      const apiParams = validatePaginationParams({
        ...params,
        limit: optimizedLimit,
        ...(token && { nextToken: token })
      });

      debugLog(`Fetching page ${pageNumber}`, { 
        endpoint, 
        apiParams, 
        isV2Enabled, 
        shouldAppend,
        requestId: currentRequestId
      });

      let normalizedResponse;

      // Try V2 API if enabled
      if (isV2Enabled) {
        try {
          const response = await API.get(endpoint, { 
            params: apiParams,
            signal: abortControllerRef.current.signal
          });
          
          // Enhanced debug logging for V2 API response
          if (FEATURES.DEBUG.LOG_PAGINATION && process.env.NODE_ENV === 'development') {
            debugLog('📦 V2 API Raw Response:', {
              status: response.status,
              dataType: Array.isArray(response.data) ? 'ARRAY' : 'OBJECT',
              dataKeys: response.data ? Object.keys(response.data) : [],
              productsCount: Array.isArray(response.data) ? response.data.length : (response.data.products?.length || 0),
              hasPaginationField: !!response.data.pagination,
              hasNextToken: !!(response.data.nextToken || response.data.pagination?.nextToken),
              hasMoreField: !!(response.data.hasMore || response.data.pagination?.hasMore)
            });
          }
          
          // Verify this is still the current request
          if (currentRequestId !== requestIdRef.current) return;
          
          normalizedResponse = normalizeV2Response(response);
          retryCountRef.current = 0; // Reset retry count on success
          
        } catch (v2Error) {
          // If request was aborted or canceled, don't continue
          if (isCancellationError(v2Error)) {
            debugLog('V2 request canceled');
            return;
          }
          
          debugLog('V2 API failed', v2Error);
          
          // Check if we should fallback to V1
          if (FEATURES.FALLBACK.ENABLE_V1_FALLBACK && 
              retryCountRef.current < FEATURES.FALLBACK.MAX_RETRY_ATTEMPTS) {
            retryCountRef.current++;
            normalizedResponse = await fallbackToV1(params);
          } else {
            throw v2Error;
          }
        }
      } else {
        // Use V1 fallback directly if V2 is disabled
        normalizedResponse = await fallbackToV1(params);
      }

      // Verify this is still the current request
      if (currentRequestId !== requestIdRef.current) return;

      const { products: newProducts, pagination } = normalizedResponse;

      debugLog('📦 Normalized response processed:', {
        newProductsCount: newProducts?.length || 0,
        hasMore: pagination.hasMore,
        nextToken: pagination.nextToken ? '[TOKEN_PRESENT]' : null,
        total: pagination.total,
        shouldAppend
      });

      // Check if we need to auto-load more pages due to server-side pagination limitations
      let finalProducts = newProducts;
      let finalPagination = pagination;
      
      // Only auto-load for V2 APIs when we get fewer products than expected and there's more data available
      if (isV2Enabled && 
          !shouldAppend && // Only on initial page loads, not load-more clicks
          newProducts.length < optimizedLimit && 
          pagination.hasMore && 
          pagination.nextToken) {
        
        debugLog('🔄 Auto-loading more pages due to insufficient results:', {
          received: newProducts.length,
          expected: optimizedLimit,
          hasMore: pagination.hasMore
        });
        
        try {
          const autoLoadResult = await loadUntilTargetReached(
            optimizedLimit, 
            newProducts, 
            pagination.nextToken, 
            2
          );
          
          finalProducts = autoLoadResult.products;
          finalPagination = {
            hasMore: autoLoadResult.hasMore,
            nextToken: autoLoadResult.nextToken,
            total: pagination.total
          };
          
          debugLog('✅ Auto-load completed:', {
            totalLoaded: finalProducts.length,
            finalHasMore: finalPagination.hasMore
          });
          
        } catch (autoLoadError) {
          debugLog('⚠️ Auto-load failed, using partial results:', autoLoadError);
          // Continue with the partial results we have
        }
      }

      // Update state
      setProducts(prev => {
        const result = shouldAppend 
          ? mergePaginatedData(prev, finalProducts, 'append')
          : finalProducts;
        
        debugLog('🔄 Products state update:', {
          prevCount: prev?.length || 0,
          newCount: finalProducts?.length || 0,
          resultCount: result?.length || 0,
          shouldAppend,
          autoLoadUsed: finalProducts.length > newProducts.length
        });
        
        return result;
      });
      setHasMore(finalPagination.hasMore);
      setNextToken(finalPagination.nextToken);
      setCurrentPage(pageNumber);
      
      if (finalPagination.total !== undefined) {
        setTotalItems(finalPagination.total);
      }

      // Cache the response (use final results for caching)
      if (useCache && !shouldAppend) {
        const cacheKey = getCacheKey(pageNumber);
        const cacheData = {
          products: finalProducts,
          pagination: finalPagination
        };
        paginationCache.set(cacheKey, cacheData, FEATURES.PAGINATION_SETTINGS.CACHE_TTL);
        
        if (FEATURES.DEBUG.LOG_CACHE_OPERATIONS) {
          debugLog(`Cached page ${pageNumber}`, { 
            cacheKey, 
            productCount: finalProducts.length,
            autoLoadUsed: finalProducts.length > newProducts.length
          });
        }
      }

    } catch (fetchError) {
      // Don't set error state if request was aborted or canceled
      if (isCancellationError(fetchError)) {
        debugLog('Request canceled');
        return;
      }

      debugLog('Fetch failed', fetchError);
      
      // Verify this is still the current request
      if (currentRequestId === requestIdRef.current) {
        setError(fetchError);
      }
    } finally {
      // Verify this is still the current request before updating loading states
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [
    endpoint,
    stableParamsString,
    optimizedLimit,
    useCache,
    isV2Enabled,
    getCacheKey,
    abortCurrentRequest,
    fallbackToV1,
    debugLog
  ]);

  // Helper function to load multiple pages when server returns fewer items than expected
  const loadUntilTargetReached = useCallback(async (targetCount, currentProducts = [], currentToken = null, pageNum = 1) => {
    // Safety limits to prevent infinite loops
    const MAX_AUTO_LOAD_PAGES = 10;
    const MAX_AUTO_LOAD_REQUESTS = 5;
    
    if (currentProducts.length >= targetCount || 
        !currentToken || 
        pageNum > MAX_AUTO_LOAD_PAGES) {
      return { 
        products: currentProducts, 
        hasMore: !!currentToken, 
        nextToken: currentToken 
      };
    }

    try {
      // Check if request was cancelled
      if (abortControllerRef.current?.signal.aborted) {
        debugLog('Auto-load cancelled');
        return { products: currentProducts, hasMore: false, nextToken: null };
      }

      const apiParams = validatePaginationParams({
        ...params,
        limit: optimizedLimit,
        nextToken: currentToken
      });

      debugLog(`🔄 Auto-loading page ${pageNum}:`, {
        currentCount: currentProducts.length,
        targetCount,
        remaining: targetCount - currentProducts.length
      });

      const response = await API.get(endpoint, { 
        params: apiParams,
        signal: abortControllerRef.current?.signal
      });

      // Check again if request was cancelled during the API call
      if (abortControllerRef.current?.signal.aborted) {
        debugLog('Auto-load cancelled during API call');
        return { products: currentProducts, hasMore: false, nextToken: null };
      }

      const normalizedResponse = normalizeV2Response(response);
      const newProducts = [...currentProducts, ...normalizedResponse.products];
      
      debugLog(`✅ Auto-loaded page ${pageNum} completed:`, {
        pageProductsCount: normalizedResponse.products.length,
        totalCount: newProducts.length,
        targetCount,
        hasMore: normalizedResponse.pagination.hasMore,
        nextToken: normalizedResponse.pagination.nextToken ? '[TOKEN_PRESENT]' : null,
        willContinue: newProducts.length < targetCount && normalizedResponse.pagination.hasMore
      });

      // If we still haven't reached target and there are more pages, continue loading
      // But add a reasonable limit to prevent too many requests
      if (newProducts.length < targetCount && 
          normalizedResponse.pagination.hasMore && 
          normalizedResponse.pagination.nextToken &&
          pageNum < MAX_AUTO_LOAD_REQUESTS) {
        return await loadUntilTargetReached(
          targetCount, 
          newProducts, 
          normalizedResponse.pagination.nextToken, 
          pageNum + 1
        );
      }

      return {
        products: newProducts,
        hasMore: normalizedResponse.pagination.hasMore,
        nextToken: normalizedResponse.pagination.nextToken
      };
    } catch (error) {
      // Handle cancellation gracefully
      if (isCancellationError(error)) {
        debugLog('Auto-load request cancelled');
        return { products: currentProducts, hasMore: false, nextToken: null };
      }
      
      debugLog('⚠️ Auto-load failed:', error);
      return { products: currentProducts, hasMore: false, nextToken: null };
    }
  }, [endpoint, params, optimizedLimit, debugLog]);

  // Load more function for infinite scroll
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore || !nextToken) {
      return;
    }

    debugLog('Loading more products', { currentPage: currentPage + 1, nextToken });
    fetchPageRef.current(currentPage + 1, nextToken, true);
  }, [loadingMore, hasMore, nextToken, currentPage, debugLog]); // Remove fetchPage dependency

  // Refetch function
  const refetch = useCallback((clearCache = false) => {
    debugLog('Refetching products', { clearCache });
    
    if (clearCache && useCache) {
      // Clear relevant cache entries
      for (let i = 1; i <= currentPage; i++) {
        const cacheKey = getCacheKey(i);
        paginationCache.remove(cacheKey);
      }
    }

    // Reset state
    setProducts([]);
    setCurrentPage(1);
    setNextToken(null);
    setHasMore(true);
    setError(null);
    setUsingV1Fallback(false);
    retryCountRef.current = 0;

    // Fetch first page
    fetchPageRef.current(1, null, false);
  }, [useCache, currentPage, getCacheKey, debugLog]); // Remove fetchPage dependency

  // Auto-load effect - use a ref to track if initial load has been done
  const hasAutoLoadedRef = useRef(false);
  const fetchPageRef = useRef();
  fetchPageRef.current = fetchPage;
  
  useEffect(() => {
    if (autoLoad && !hasAutoLoadedRef.current) {
      hasAutoLoadedRef.current = true;
      fetchPageRef.current(1, null, false);
    }

    // Cleanup on unmount
    return () => {
      abortCurrentRequest();
    };
  }, [autoLoad]); // Only depend on autoLoad
  
  // Track previous params to detect actual changes
  const prevParamsRef = useRef('');
  const prevEndpointRef = useRef('');
  
  // Reset and refetch when key parameters change
  useEffect(() => {
    if (hasAutoLoadedRef.current) {
      const hasEndpointChanged = endpoint !== prevEndpointRef.current;
      const hasParamsChanged = stableParamsString !== prevParamsRef.current;
      
      // Only refetch if there's an actual meaningful change
      if (hasEndpointChanged || hasParamsChanged) {
        debugLog('Parameters changed, refetching', {
          endpointChanged: hasEndpointChanged,
          paramsChanged: hasParamsChanged,
          oldParams: prevParamsRef.current,
          newParams: stableParamsString,
          oldEndpoint: prevEndpointRef.current,
          newEndpoint: endpoint
        });
        
        // Update refs with new values
        prevParamsRef.current = stableParamsString;
        prevEndpointRef.current = endpoint;
        
        // Reset state and fetch again when parameters change
        setProducts([]);
        setCurrentPage(1);
        setNextToken(null);
        setHasMore(true);
        setError(null);
        setUsingV1Fallback(false);
        retryCountRef.current = 0;
        fetchPageRef.current(1, null, false);
      }
    } else {
      // Initialize refs on first load
      prevParamsRef.current = stableParamsString;
      prevEndpointRef.current = endpoint;
    }
  }, [endpoint, stableParamsString, debugLog]); // Only when endpoint or params change

  // Create pagination metadata
  const paginationMeta = useMemo(() => 
    createPaginationMeta({
      currentPage,
      hasMore,
      nextToken,
      itemsPerPage: optimizedLimit,
      totalItems
    }), [currentPage, hasMore, nextToken, optimizedLimit, totalItems]
  );

  // Performance metrics (if debug enabled)
  const performanceMetrics = useMemo(() => {
    if (!FEATURES.DEBUG.SHOW_PERFORMANCE_METRICS) return null;
    
    return {
      totalProducts: products.length,
      currentPage,
      usingV1Fallback,
      cacheEnabled: useCache,
      v2Enabled: isV2Enabled,
      retryCount: retryCountRef.current
    };
  }, [products.length, currentPage, usingV1Fallback, useCache, isV2Enabled]);

  return {
    // Data
    products,
    
    // State
    loading,
    loadingMore,
    error,
    hasMore,
    usingV1Fallback,
    
    // Pagination info
    currentPage,
    nextToken,
    totalItems,
    pagination: paginationMeta,
    
    // Actions
    loadMore,
    refetch,
    
    // Debug info
    ...(FEATURES.DEBUG.SHOW_PERFORMANCE_METRICS && { 
      performanceMetrics 
    })
  };
}
